import { useQuery } from '@tanstack/react-query'
import { useContext, useMemo } from 'react'
import { DCFilter } from 'types'
import { getAggregations } from 'utils'
import { OnTheGoContext } from '../components'

export const useOnTheGo = () => useContext(OnTheGoContext)

const sessionsWithoutEventFilter: DCFilter[] = [
  { field: 'eventId', operator: 'isNull', table: 'leadSessions', value: true },
]

const leadsWithoutEventFilters: DCFilter[] = [
  { field: 'eventId', operator: 'isNull', table: 'leads', value: true },
]

export interface OnTheGoMemberStats {
  leadCount: number
  sessionCount: number
}

async function fetchOnTheGoStatsForWorkspace(
  workspaceId: string,
  memberIdSet: Set<string>
): Promise<Map<string, OnTheGoMemberStats>> {
  const stats = new Map<string, OnTheGoMemberStats>()

  const res = await getAggregations({
    aggregations: [
      {
        categories: ['createdBy'],
        filters: sessionsWithoutEventFilter,
        table: 'leadSessions',
        values: ['_count'],
      },
      {
        categories: ['createdBy'],
        filters: leadsWithoutEventFilters,
        table: 'leads',
        values: ['_count'],
      },
    ],
    workspaceIds: [workspaceId],
  })

  for (const row of res.data[0] ?? []) {
    const uid = row.createdBy as string | null | undefined
    if (!uid || !memberIdSet.has(uid)) continue
    const cur = stats.get(uid) || { leadCount: 0, sessionCount: 0 }
    cur.sessionCount = Number(row._count) || 0
    stats.set(uid, cur)
  }

  for (const row of res.data[1] ?? []) {
    const uid = row.createdBy as string | null | undefined
    if (!uid || !memberIdSet.has(uid)) continue
    const cur = stats.get(uid) || { leadCount: 0, sessionCount: 0 }
    cur.leadCount = Number(row._count) || 0
    stats.set(uid, cur)
  }

  return stats
}

export function useOnTheGoMemberStats(workspaceId: string | undefined, memberIds: string[]) {
  const memberIdSet = useMemo(() => new Set(memberIds.filter(Boolean)), [memberIds])
  const memberIdsKey = useMemo(() => [...memberIdSet].sort().join(','), [memberIdSet])

  return useQuery({
    enabled: Boolean(workspaceId) && memberIdSet.size > 0,
    queryFn: () => fetchOnTheGoStatsForWorkspace(workspaceId!, memberIdSet),
    queryKey: ['onTheGoMemberStats', workspaceId, memberIdsKey],
    staleTime: 1000 * 60,
  })
}

export function getOtgMemberSessionFilters(memberId: string): DCFilter[] {
  return [
    { field: 'createdBy', operator: 'eq', table: 'leadSessions', value: memberId },
    { field: 'eventId', operator: 'isNull', table: 'leadSessions', value: true },
  ]
}
